import "./styles.css";
import "./theme.css";

import {
  AXIAL_DIRECTIONS,
  addCoords,
  coordKey,
  hexCenter,
  hexPoints,
  siteViewBox,
} from "./hex";
import {
  SAVE_FORMAT_VERSION,
  SAVE_STORAGE_KEY,
  THEME_STORAGE_KEY,
  parseSavedGame,
  parseThemePreference,
  type CommittedAction,
  type SavedGame,
  type ThemePreference,
} from "./persistence";
import {
  ACTIONS,
  CROPS,
  MINIMUM_TERMINAL_COST,
  TERMINAL_ACTION_IDS,
  calculateSiteResult,
  maximumCellValue,
  resolveSurvey,
  resolveTerminalAction,
  soilName,
} from "./rules";
import { SITE } from "./sites";
import type {
  ActionId,
  Cell,
  CellKnowledge,
  Difficulty,
  JournalLayer,
  JournalState,
  LedgerEntry,
  SiteResult,
  TerminalActionId,
} from "./types";

type Phase = "setup" | "playing" | "failed" | "complete";
type ResetIntent = "new" | "restart";
type JournalEdit = Readonly<{
  layer: JournalLayer;
  cellKey: string;
  wasMarked: boolean;
}>;

const appElement = document.querySelector<HTMLDivElement>("#app");
if (appElement === null) {
  throw new Error("Missing #app element.");
}
const app: HTMLDivElement = appElement;

let phase: Phase = "setup";
let difficulty: Difficulty = "standard";
let openingFunds = SITE.startingFunds.standard;
let funds = openingFunds;
let selectedKey = SITE.cells[12]?.key ?? SITE.cells[0]?.key ?? "";
let pendingAction: ActionId | undefined;
let knowledge = new Map<string, CellKnowledge>();
let ledger: LedgerEntry[] = [];
let journal: Record<JournalLayer, Set<string>> = emptyJournal();
let statusMessage =
  "Select a location to inspect its surface, then choose how to develop it.";
let finalResult: SiteResult | undefined;
let resultsOpen = false;
let finishWarningOpen = false;
let failureOpen = false;
let resetIntent: ResetIntent | undefined;
let resetReturnFocus: string | undefined;
let committedActions: CommittedAction[] = [];
let hintSteps = new Map<string, number>();
let journalUndo: JournalEdit[] = [];
let journalRedo: JournalEdit[] = [];

const cellKeys = new Set(SITE.cells.map((cell) => cell.key));
const saveIdentity = {
  siteId: SITE.id,
  schemaVersion: SITE.schemaVersion,
  rulesetVersion: SITE.rulesetVersion,
  contentVersion: SITE.contentVersion,
  cellKeys,
};

function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

let savedGame = parseSavedGame(readStorage(SAVE_STORAGE_KEY), saveIdentity);
let themePreference: ThemePreference = parseThemePreference(
  readStorage(THEME_STORAGE_KEY),
);

function resolvedTheme(preference: ThemePreference): "dark" | "light" {
  if (preference !== "automatic") return preference;
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function applyTheme(): void {
  const theme = resolvedTheme(themePreference);
  document.documentElement.dataset.theme = theme;
  document.documentElement.dataset.themePreference = themePreference;
  document.documentElement.style.colorScheme = theme;
  document
    .querySelector<HTMLMetaElement>('meta[name="theme-color"]')
    ?.setAttribute("content", theme === "dark" ? "#090a0c" : "#f3efe5");
}

applyTheme();
window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
  if (themePreference === "automatic") applyTheme();
});

function emptyJournal(): Record<JournalLayer, Set<string>> {
  return {
    water: new Set<string>(),
    road: new Set<string>(),
    interest: new Set<string>(),
  };
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function formatFunds(value: number): string {
  return `${value}¤`;
}

function formatSignedFunds(value: number): string {
  if (value > 0) return `+${value}¤`;
  return `${value}¤`;
}

function percent(value: number): string {
  return `${Math.round(value)}%`;
}

function writeStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    statusMessage = "Your browser blocked local saving; this field will last only for this tab.";
  }
}

function removeStorage(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // A blocked storage implementation is already handled by writeStorage.
  }
}

function savedJournal(): SavedGame["journal"] {
  return {
    water: [...journal.water],
    road: [...journal.road],
    interest: [...journal.interest],
  };
}

function persistGame(): void {
  if (phase === "setup") return;
  const snapshot: SavedGame = {
    formatVersion: SAVE_FORMAT_VERSION,
    siteId: SITE.id,
    schemaVersion: SITE.schemaVersion,
    rulesetVersion: SITE.rulesetVersion,
    contentVersion: SITE.contentVersion,
    phase,
    difficulty,
    selectedKey,
    committedActions: [...committedActions],
    journal: savedJournal(),
    hintSteps: Object.fromEntries(hintSteps),
  };
  writeStorage(SAVE_STORAGE_KEY, JSON.stringify(snapshot));
  savedGame = snapshot;
}

function clearSavedGame(): void {
  removeStorage(SAVE_STORAGE_KEY);
  savedGame = undefined;
}

function renderThemeControl(): string {
  return `
    <label class="theme-control">
      <span>Theme</span>
      <select data-theme-control aria-label="Color theme">
        ${(["automatic", "dark", "light"] as const)
          .map(
            (theme) =>
              `<option value="${theme}" ${theme === themePreference ? "selected" : ""}>${theme[0]?.toUpperCase()}${theme.slice(1)}</option>`,
          )
          .join("")}
      </select>
    </label>
  `;
}

function replayCommittedAction(item: CommittedAction): boolean {
  const cell = SITE.cellsByKey.get(item.cellKey);
  if (cell === undefined) return false;
  const entry = knowledgeFor(cell.key);
  const action = ACTIONS[item.action];
  if (entry.terminal !== undefined || funds < action.cost) return false;

  if (item.action === "survey") {
    if (entry.surveyed) return false;
    const survey = resolveSurvey(cell, SITE);
    funds -= survey.cost;
    knowledge.set(cell.key, {
      surveyed: true,
      surveyEvidence: survey.evidence,
    });
    recordLedger(cell, item.action, "low-impact survey", survey.cost, 0);
    return true;
  }

  const terminal = resolveTerminalAction(item.action, cell, SITE);
  funds = funds - terminal.cost + terminal.revenue;
  knowledge.set(cell.key, {
    surveyed: entry.surveyed,
    surveyEvidence: entry.surveyEvidence,
    terminal,
  });
  recordLedger(
    cell,
    item.action,
    terminal.outcomeTitle,
    terminal.cost,
    terminal.revenue,
  );
  return true;
}

function continueSavedGame(): boolean {
  if (savedGame === undefined) return false;
  const snapshot = savedGame;
  difficulty = snapshot.difficulty;
  openingFunds = SITE.startingFunds[difficulty];
  funds = openingFunds;
  selectedKey = snapshot.selectedKey;
  pendingAction = undefined;
  knowledge = new Map();
  ledger = [];
  committedActions = [];
  journal = emptyJournal();
  hintSteps = new Map(Object.entries(snapshot.hintSteps));
  journalUndo = [];
  journalRedo = [];
  finalResult = undefined;
  resultsOpen = false;
  finishWarningOpen = false;
  failureOpen = false;
  resetIntent = undefined;

  for (const item of snapshot.committedActions) {
    if (!replayCommittedAction(item)) {
      clearSavedGame();
      phase = "setup";
      return false;
    }
    committedActions.push(item);
  }
  journal = {
    water: new Set(snapshot.journal.water),
    road: new Set(snapshot.journal.road),
    interest: new Set(snapshot.journal.interest),
  };

  const isInsolvent = !allLocationsTerminal() && funds < MINIMUM_TERMINAL_COST;
  if (
    (snapshot.phase === "failed" && !isInsolvent) ||
    (snapshot.phase === "complete" && !allLocationsTerminal()) ||
    (snapshot.phase === "playing" && isInsolvent)
  ) {
    clearSavedGame();
    phase = "setup";
    return false;
  }

  phase = snapshot.phase;
  if (phase === "complete") {
    finalResult = calculateSiteResult(
      SITE,
      openingFunds,
      knowledge,
      ledger,
      currentJournal(),
    );
    statusMessage = "Your completed field and final analysis were restored.";
  } else if (phase === "failed") {
    failureOpen = true;
    statusMessage = "Your insolvent field was restored for review or replay.";
  } else {
    statusMessage = "Your saved field was restored. Continue when you are ready.";
  }
  return true;
}

function currentJournal(): JournalState {
  return journal;
}

function knowledgeFor(key: string): CellKnowledge {
  return (
    knowledge.get(key) ?? {
      surveyed: false,
      surveyEvidence: [],
    }
  );
}

function terminalCount(): number {
  return [...knowledge.values()].filter((entry) => entry.terminal !== undefined)
    .length;
}

function allLocationsTerminal(): boolean {
  return terminalCount() === SITE.cells.length;
}

function selectedCell(): Cell {
  const cell = SITE.cellsByKey.get(selectedKey);
  if (cell === undefined) {
    throw new Error(`Unknown selected cell ${selectedKey}.`);
  }
  return cell;
}

function startGame(selectedDifficulty: Difficulty): void {
  difficulty = selectedDifficulty;
  openingFunds = SITE.startingFunds[difficulty];
  funds = openingFunds;
  selectedKey = SITE.cells[12]?.key ?? SITE.cells[0]?.key ?? "";
  pendingAction = undefined;
  knowledge = new Map();
  ledger = [];
  journal = emptyJournal();
  committedActions = [];
  hintSteps = new Map();
  journalUndo = [];
  journalRedo = [];
  finalResult = undefined;
  resultsOpen = false;
  finishWarningOpen = false;
  failureOpen = false;
  resetIntent = undefined;
  phase = "playing";
  statusMessage =
    "The field is yours to develop. Surface observations are free; every committed action matters.";
  render();
}

function actionSymbol(action: TerminalActionId): string {
  if (action === "oats") return "O";
  if (action === "wheat") return "W";
  if (action === "alfalfa") return "A";
  if (action === "excavate") return "X";
  return "P";
}

function actionName(action: TerminalActionId): string {
  return ACTIONS[action].name;
}

function surfaceMark(cell: Cell): string {
  if (cell.surface === "reed-swale") return "≈";
  if (cell.surface === "lush-grass") return "〽";
  if (cell.surface === "short-grass") return "—";
  if (cell.surface === "stony-rise") return "•";
  return "·";
}

function renderSetup(): string {
  return `
    <main class="landing">
      <section class="landing-card" aria-labelledby="game-title">
        <div class="landing-tools">${renderThemeControl()}</div>
        <p class="eyebrow">A farming and landscape deduction prototype</p>
        <h1 id="game-title">Where Rivers Ran</h1>
        <p class="landing-lead">
          Cultivate a neglected field. Every crop, survey, excavation, and
          preservation decision changes its final value—and reveals why the
          land behaves as it does.
        </p>
        <div class="premise-grid">
          <article>
            <span class="premise-number">1</span>
            <h2>Read the surface</h2>
            <p>Grass, stones, hollows, and rises are meaningful clues.</p>
          </article>
          <article>
            <span class="premise-number">2</span>
            <h2>Commit the land</h2>
            <p>Planting, excavation, and preservation permanently finish a location.</p>
          </article>
          <article>
            <span class="premise-number">3</span>
            <h2>Recover its history</h2>
            <p>Use the free Field Journal to mark the watercourse, road, and buried places you infer.</p>
          </article>
        </div>
        <fieldset class="difficulty-picker">
          <legend>Choose your operating funds</legend>
          ${renderDifficultyOption(
            "easy",
            "Generous",
            "Room for several uncertain surveys and experiments.",
          )}
          ${renderDifficultyOption(
            "standard",
            "Balanced",
            "Enough flexibility for a reasoned investigation and some mistakes.",
          )}
          ${renderDifficultyOption(
            "hard",
            "Exacting",
            "A small margin. Failed excavations or unfocused surveys may end the attempt.",
          )}
        </fieldset>
        <div class="landing-actions">
          ${
            savedGame === undefined
              ? ""
              : `<button class="primary-button start-button" data-continue-game type="button">Continue saved field</button>`
          }
          <button class="${savedGame === undefined ? "primary-button" : "quiet-button"} start-button" data-start-game type="button">
            ${savedGame === undefined ? "Begin" : "Start a new field at"} ${escapeHtml(SITE.title)}
          </button>
        </div>
        <p class="calm-note">There are no timers. Take as long as you want between actions.</p>
      </section>
    </main>
    ${resetIntent === undefined ? "" : renderResetConfirmation()}
  `;
}

function renderDifficultyOption(
  id: Difficulty,
  title: string,
  description: string,
): string {
  const checked = id === difficulty ? "checked" : "";
  return `
    <label class="difficulty-option">
      <input type="radio" name="difficulty" value="${id}" ${checked}>
      <span>
        <strong>${title}</strong>
        <small>${formatFunds(SITE.startingFunds[id])} — ${description}</small>
      </span>
    </label>
  `;
}

function renderHeader(): string {
  return `
    <header class="game-header">
      <div>
        <p class="eyebrow">Field investigation · ${escapeHtml(difficulty)}</p>
        <h1>Where Rivers Ran</h1>
      </div>
      <div class="header-metrics" aria-label="Current field status">
        ${renderThemeControl()}
        <div class="metric funds-metric">
          <span>Available funds</span>
          <strong data-funds>${formatFunds(funds)}</strong>
        </div>
        <div class="metric">
          <span>Locations finished</span>
          <strong data-terminal-count>${terminalCount()} / ${SITE.cells.length}</strong>
        </div>
        ${
          phase === "complete"
            ? `<button class="primary-button" data-open-results type="button">View final report</button>`
            : ""
        }
        <button class="quiet-button" data-new-game type="button">New field</button>
      </div>
    </header>
  `;
}

function renderMap(): string {
  const box = siteViewBox(SITE.cells.map((cell) => cell.coord));
  const reveal = phase === "complete";
  const roadPoints = SITE.roadPath
    .map((key) => SITE.cellsByKey.get(key))
    .filter((cell): cell is Cell => cell !== undefined)
    .map((cell) => {
      const center = hexCenter(cell.coord);
      return `${center.x},${center.y}`;
    })
    .join(" ");
  const waterPoints = SITE.watercoursePath
    .map((key) => SITE.cellsByKey.get(key))
    .filter((cell): cell is Cell => cell !== undefined)
    .map((cell) => {
      const center = hexCenter(cell.coord);
      return `${center.x},${center.y}`;
    })
    .join(" ");

  return `
    <section class="map-card" aria-labelledby="site-title">
      <div class="map-heading">
        <div>
          <p class="eyebrow">Current site</p>
          <h2 id="site-title">${escapeHtml(SITE.title)}</h2>
          <p>${escapeHtml(SITE.subtitle)}</p>
        </div>
        ${renderMapLegend(reveal)}
      </div>
      ${renderJournalToolbar()}
      <div class="map-frame">
        <svg
          id="game-map"
          class="field-map ${reveal ? "is-revealed" : ""}"
          viewBox="${box.x} ${box.y} ${box.width} ${box.height}"
          role="group"
          aria-label="${escapeHtml(SITE.title)} field map"
          aria-describedby="map-instructions"
        >
          ${SITE.cells.map(renderCell).join("")}
          ${
            reveal
              ? `
                <polyline class="truth-road" points="${roadPoints}" />
                <polyline class="truth-water" points="${waterPoints}" />
                ${SITE.pointsOfInterest
                  .map((point) => {
                    const cell = SITE.cellsByKey.get(point.cellKey);
                    if (cell === undefined) return "";
                    const center = hexCenter(cell.coord);
                    return `<g class="truth-interest" transform="translate(${center.x} ${center.y})">
                      <circle r="12"></circle><text y="4">✦</text>
                    </g>`;
                  })
                  .join("")}
              `
              : ""
          }
        </svg>
      </div>
      <p class="map-instruction" id="map-instructions">
        Tab to the selected location, use the arrow keys to move across the field,
        and press Enter or Space to inspect. Surface inspection and Field Journal marks are free.
      </p>
    </section>
  `;
}

function renderMapLegend(reveal: boolean): string {
  if (reveal) {
    return `
      <div class="map-key reveal-key" aria-label="Revealed history map key">
        <span><i class="line-key water-line-key"></i> former watercourse</span>
        <span><i class="line-key road-line-key"></i> old farm road</span>
        <span><i class="key-mark interest-key">✦</i> point of interest</span>
      </div>
    `;
  }
  return `
    <div class="map-key" aria-label="Map key">
      <span><i class="key-mark surface-key">·</i> surface clue</span>
      <span><i class="key-mark survey-key">●</i> surveyed</span>
      <span><i class="key-mark journal-key">~</i> your Journal hypothesis</span>
    </div>
  `;
}

function renderCell(cell: Cell): string {
  const center = hexCenter(cell.coord);
  const entry = knowledgeFor(cell.key);
  const terminal = entry.terminal;
  const classes = [
    "map-cell",
    `surface-${cell.surface}`,
    cell.key === selectedKey ? "is-selected" : "",
    terminal === undefined ? "" : `terminal-${terminal.action}`,
    entry.surveyed ? "is-surveyed" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const journalMarks = [
    journal.water.has(cell.key) ? "~" : "",
    journal.road.has(cell.key) ? "—" : "",
    journal.interest.has(cell.key) ? "?" : "",
  ].join("");
  const terminalLabel =
    terminal === undefined
      ? surfaceMark(cell)
      : actionSymbol(terminal.action);
  const accessibleState =
    terminal === undefined
      ? `${cell.surfaceObservation}${entry.surveyed ? " Surveyed." : ""}`
      : `${actionName(terminal.action)}. ${terminal.outcomeTitle}.`;

  return `
    <g
      class="${classes}"
      data-cell-key="${cell.key}"
      role="button"
      tabindex="${cell.key === selectedKey ? "0" : "-1"}"
      aria-pressed="${cell.key === selectedKey}"
      aria-label="Location ${cell.label}. ${escapeHtml(accessibleState)}"
    >
      <polygon points="${hexPoints(cell.coord)}"></polygon>
      <text class="cell-label" x="${center.x}" y="${center.y - 22}">${cell.label}</text>
      <text class="cell-symbol" x="${center.x}" y="${center.y + 7}">${terminalLabel}</text>
      ${entry.surveyed ? `<circle class="survey-dot" cx="${center.x + 24}" cy="${center.y - 21}" r="4"></circle>` : ""}
      ${
        journalMarks.length > 0
          ? `<text class="journal-marks" x="${center.x}" y="${center.y + 28}">${journalMarks}</text>`
          : ""
      }
    </g>
  `;
}

function renderSelectedLocation(): string {
  const cell = selectedCell();
  const entry = knowledgeFor(cell.key);
  const evidence = [
    ...entry.surveyEvidence,
    ...(entry.terminal?.evidence ?? []),
  ];

  return `
    <section class="location-panel" aria-labelledby="location-heading">
      <div class="location-heading">
        <div>
          <p class="eyebrow">Selected location</p>
          <h2 id="location-heading">${cell.label}</h2>
        </div>
        <span class="elevation-chip">Elevation ${cell.elevation}</span>
      </div>
      <div class="observation-card">
        <span>Surface observation</span>
        <p>${escapeHtml(cell.surfaceObservation)}</p>
      </div>
      ${
        entry.terminal === undefined
          ? renderAvailableActions(cell, entry)
          : renderTerminalOutcome(entry)
      }
      ${phase === "playing" ? renderHintPanel(cell, entry) : ""}
      ${phase === "complete" ? renderRevealedAnalysis(cell, entry) : ""}
      <div class="evidence-section">
        <h3>${phase === "complete" ? "Evidence gathered during play" : "Known evidence"}</h3>
        ${
          evidence.length === 0
            ? `<p class="empty-copy">No subsurface evidence yet. The visible surface still matters.</p>`
            : evidence
                .map(
                  (item) => `
                    <article class="evidence-item">
                      <strong>${escapeHtml(item.title)}</strong>
                      <p>${escapeHtml(item.detail)}</p>
                    </article>
                  `,
                )
                .join("")
        }
      </div>
    </section>
  `;
}

function surfaceReasoning(cell: Cell): string {
  if (cell.surface === "reed-swale") {
    return "Rushes in a low swale support a water-related hypothesis, but do not prove the exact former channel. Look for a connected continuation in neighboring locations.";
  }
  if (cell.surface === "lush-grass") {
    return "Unusually vigorous grass can reflect nearby moisture rather than water directly beneath this location. Compare its neighbors before marking a route.";
  }
  if (cell.surface === "short-grass") {
    return "Repeated short or compacted-looking ground may form a human route. A connected line is stronger evidence than one isolated location.";
  }
  if (cell.surface === "stony-rise") {
    return "A stony rise makes expensive cultivation risky. Gather evidence or compare the low-cost outcomes before committing the location.";
  }
  return "This meadow is a relatively neutral clue. Use neighboring patterns and evidence from earlier actions instead of treating it as proof by itself.";
}

function renderHintPanel(cell: Cell, entry: CellKnowledge): string {
  const step = hintSteps.get(cell.key) ?? 0;
  let hint = "";
  if (step === 1) {
    hint = `Start with only what is visible: ${cell.surfaceObservation}`;
  } else if (step === 2) {
    hint = surfaceReasoning(cell);
  } else if (step >= 3) {
    hint =
      entry.surveyEvidence.length > 0
        ? `Connect the evidence you already earned: ${entry.surveyEvidence
            .slice(0, 2)
            .map((item) => `${item.title} — ${item.detail}`)
            .join(" ")}`
        : "If the surface and neighboring pattern do not support one explanation, a low-impact survey is the information-producing action that leaves this location available. Check its displayed cost before committing.";
  }
  return `
    <section class="hint-panel" aria-labelledby="hint-heading">
      <div class="section-heading">
        <h3 id="hint-heading">Reasoning help</h3>
        <span>Uses visible and earned information only</span>
      </div>
      ${
        step === 0
          ? `<p>Hints are graduated and never inspect the hidden solution.</p>`
          : `<p class="hint-text" aria-live="polite"><b>Step ${step} of 3.</b> ${escapeHtml(hint)}</p>`
      }
      <button class="quiet-button compact" data-hint type="button" ${step >= 3 ? "disabled" : ""}>
        ${step === 0 ? "Show first hint" : step >= 3 ? "All hints shown" : "Show next hint"}
      </button>
    </section>
  `;
}

function renderAvailableActions(
  cell: Cell,
  entry: CellKnowledge,
): string {
  const ids: readonly ActionId[] = [
    "oats",
    "wheat",
    "alfalfa",
    "survey",
    "excavate",
    "preserve",
  ];
  return `
    <div class="actions-section">
      <div class="section-heading">
        <h3>Choose an action</h3>
        <span>Terminal actions cannot be undone</span>
      </div>
      <div class="action-list">
        ${ids
          .map((id) => {
            const action = ACTIONS[id];
            const alreadySurveyed = id === "survey" && entry.surveyed;
            const cannotAfford = funds < action.cost;
            const disabled = alreadySurveyed || cannotAfford || phase !== "playing";
            const label = alreadySurveyed
              ? "Already surveyed"
              : cannotAfford
                ? "Cannot afford"
                : action.terminal
                  ? "Permanent"
                  : "Non-terminal";
            return `
              <button
                class="action-button action-${id}"
                data-action="${id}"
                type="button"
                ${disabled ? "disabled" : ""}
              >
                <span class="action-copy">
                  <strong>${escapeHtml(action.name)}</strong>
                  <small>${escapeHtml(action.description)}</small>
                </span>
                <span class="action-price">
                  <b>${formatFunds(action.cost)}</b>
                  <em>${escapeHtml(actionFinancialHint(id, cell, entry))}</em>
                  <small>${label}</small>
                </span>
              </button>
            `;
          })
          .join("")}
      </div>
      ${pendingAction === undefined ? "" : renderConfirmation(pendingAction)}
    </div>
  `;
}

function actionFinancialHint(
  actionId: ActionId,
  cell: Cell,
  entry: CellKnowledge,
): string {
  if (
    actionId === "oats" ||
    actionId === "wheat" ||
    actionId === "alfalfa"
  ) {
    if (entry.surveyed) {
      const result = resolveTerminalAction(actionId, cell, SITE);
      return `Known return ${formatFunds(result.revenue)} · net ${formatSignedFunds(
        result.revenue - result.cost,
      )}`;
    }
    const yields = Object.values(CROPS[actionId].yieldBySoil);
    return `Possible return ${Math.min(...yields)}–${Math.max(...yields)}¤`;
  }
  if (actionId === "survey") {
    return "No direct return";
  }
  if (actionId === "preserve") {
    if (entry.surveyed) {
      return `Known return ${formatFunds(cell.preservationReward)} · net ${formatSignedFunds(
        cell.preservationReward - ACTIONS.preserve.cost,
      )}`;
    }
    return "Return 0¤ or a qualifying award";
  }
  if (entry.surveyed && cell.pointOfInterestId !== undefined) {
    return "Survey indicates a buried feature";
  }
  return "Return 0¤ or a discovery award";
}

function renderConfirmation(actionId: ActionId): string {
  const action = ACTIONS[actionId];
  const warning = action.terminal
    ? "This permanently finishes the selected location."
    : "The location remains available for one terminal action.";
  return `
    <div class="confirmation-card" role="alert">
      <strong>Confirm ${escapeHtml(action.name)}?</strong>
      <p>${warning} ${formatFunds(action.cost)} will be spent immediately.</p>
      <div>
        <button class="primary-button compact" data-confirm-action type="button">Confirm</button>
        <button class="quiet-button compact" data-cancel-action type="button">Cancel</button>
      </div>
    </div>
  `;
}

function renderTerminalOutcome(entry: CellKnowledge): string {
  const result = entry.terminal;
  if (result === undefined) return "";
  return `
    <div class="terminal-card terminal-card-${result.action}">
      <span>Terminal use · ${escapeHtml(actionName(result.action))}</span>
      <h3>${escapeHtml(result.outcomeTitle)}</h3>
      <p>${escapeHtml(result.outcomeDetail)}</p>
      <dl class="mini-balance">
        <div><dt>Spent</dt><dd>−${formatFunds(result.cost)}</dd></div>
        <div><dt>Earned</dt><dd>+${formatFunds(result.revenue)}</dd></div>
        <div><dt>Net</dt><dd>${formatSignedFunds(result.revenue - result.cost)}</dd></div>
      </dl>
    </div>
  `;
}

function renderRevealedAnalysis(cell: Cell, entry: CellKnowledge): string {
  const outcomes = TERMINAL_ACTION_IDS.map((action) =>
    resolveTerminalAction(action, cell, SITE),
  );
  const maximumValue = maximumCellValue(cell, SITE);
  const bestActions = outcomes
    .filter((outcome) => outcome.siteValue === maximumValue)
    .map((outcome) => actionName(outcome.action))
    .join(" or ");
  const point =
    cell.pointOfInterestId === undefined
      ? undefined
      : SITE.pointsOfInterestById.get(cell.pointOfInterestId);
  const survey = resolveSurvey(cell, SITE);
  const excavation = resolveTerminalAction("excavate", cell, SITE);

  return `
    <section class="revealed-analysis" aria-labelledby="truth-${cell.label}">
      <div class="revealed-heading">
        <div>
          <p class="eyebrow">Unlocked after completion</p>
          <h3 id="truth-${cell.label}">Revealed Land Analysis</h3>
        </div>
        <span class="truth-badge">Full truth</span>
      </div>
      <div class="truth-grid">
        <div><span>True soil</span><strong>${escapeHtml(soilName(cell.soil))}</strong></div>
        <div><span>Former watercourse</span><strong>${cell.isFormerWatercourse ? "Yes" : "No"}</strong></div>
        <div><span>Old farm road</span><strong>${cell.isOldRoad ? "Yes" : "No"}</strong></div>
        <div><span>Point of interest</span><strong>${escapeHtml(point?.title ?? "None")}</strong></div>
        <div><span>Preservation award</span><strong>${formatFunds(cell.preservationReward)}</strong></div>
        <div><span>Best site-value use</span><strong>${escapeHtml(bestActions)} · ${maximumValue}</strong></div>
      </div>
      <details class="analysis-details" open>
        <summary>Compare every terminal outcome</summary>
        <div class="analysis-table-wrap">
          <table class="analysis-table">
            <thead>
              <tr><th>Use</th><th>Cost</th><th>Return</th><th>Net funds</th><th>Result</th></tr>
            </thead>
            <tbody>
              ${outcomes
                .map(
                  (outcome) => `
                    <tr class="${entry.terminal?.action === outcome.action ? "is-chosen" : ""} ${outcome.siteValue === maximumValue ? "is-best" : ""}">
                      <th>${escapeHtml(actionName(outcome.action))}${entry.terminal?.action === outcome.action ? " · chosen" : ""}</th>
                      <td>−${formatFunds(outcome.cost)}</td>
                      <td>+${formatFunds(outcome.revenue)}</td>
                      <td>${formatSignedFunds(outcome.revenue - outcome.cost)}</td>
                      <td>${escapeHtml(outcome.outcomeTitle)}${outcome.siteValue === maximumValue ? " · best site value" : ""}</td>
                    </tr>
                  `,
                )
                .join("")}
            </tbody>
          </table>
        </div>
      </details>
      <details class="analysis-details">
        <summary>Compare what each investigation reveals</summary>
        <div class="information-stages">
          <article>
            <span>Initially visible</span>
            <strong>Surface observation</strong>
            <p>${escapeHtml(cell.surfaceObservation)}</p>
          </article>
          <article>
            <span>Low-impact survey · ${formatFunds(survey.cost)}</span>
            <strong>Non-terminal evidence</strong>
            ${renderEvidenceList(survey.evidence)}
          </article>
          <article>
            <span>Full excavation · ${formatFunds(excavation.cost)}</span>
            <strong>${escapeHtml(excavation.outcomeTitle)}</strong>
            ${renderEvidenceList(excavation.evidence)}
          </article>
        </div>
      </details>
    </section>
  `;
}

function renderEvidenceList(
  items: readonly Readonly<{ title: string; detail: string }>[],
): string {
  return `
    <ul class="analysis-evidence">
      ${items
        .map(
          (item) =>
            `<li><b>${escapeHtml(item.title)}</b><span>${escapeHtml(item.detail)}</span></li>`,
        )
        .join("")}
    </ul>
  `;
}

function renderJournalToolbar(): string {
  const cell = selectedCell();
  const layers: readonly [JournalLayer, string, string][] = [
    ["water", "Former water", "~"],
    ["road", "Old road", "—"],
    ["interest", "Point of interest", "?"],
  ];
  return `
    <div class="journal-toolbar" aria-label="Field Journal controls for ${cell.label}">
      <div class="journal-intro">
        <span class="eyebrow">Field Journal · ${cell.label}</span>
        <p>${
          phase === "complete"
            ? "Your submitted marks remain visible for comparison with the revealed truth."
            : "Mark what you believe lies here. These free hypotheses create your historical reconstruction."
        }</p>
      </div>
      <div class="journal-buttons">
        ${layers
          .map(([layer, label, mark]) => {
            const active = journal[layer].has(cell.key);
            return `
              <button
                class="${active ? "is-active" : ""}"
                data-journal-layer="${layer}"
                type="button"
                aria-pressed="${active}"
                ${phase === "playing" ? "" : "disabled"}
              ><b>${mark}</b>${label}</button>
            `;
          })
          .join("")}
        <button
          class="journal-history-button"
          data-journal-undo
          type="button"
          aria-keyshortcuts="Control+Z Meta+Z"
          ${phase === "playing" && journalUndo.length > 0 ? "" : "disabled"}
        ><b>↶</b>Undo mark</button>
        <button
          class="journal-history-button"
          data-journal-redo
          type="button"
          aria-keyshortcuts="Control+Shift+Z Meta+Shift+Z Control+Y"
          ${phase === "playing" && journalRedo.length > 0 ? "" : "disabled"}
        ><b>↷</b>Redo mark</button>
      </div>
    </div>
  `;
}

function renderLedger(): string {
  const recent = ledger.slice(-5).reverse();
  const spent = ledger.reduce((total, entry) => total + entry.spent, 0);
  const earned = ledger.reduce((total, entry) => total + entry.earned, 0);
  return `
    <section class="ledger-panel">
      <div class="section-heading">
        <h2>Field ledger</h2>
        <span>Net ${formatSignedFunds(earned - spent)}</span>
      </div>
      ${
        recent.length === 0
          ? `<p class="empty-copy">Your committed actions will appear here.</p>`
          : `<ol class="ledger-list">
              ${recent
                .map(
                  (entry) => `
                    <li>
                      <span><b>${entry.label}</b> ${escapeHtml(entry.description)}</span>
                      <strong class="${entry.earned - entry.spent >= 0 ? "positive" : "negative"}">
                        ${formatSignedFunds(entry.earned - entry.spent)}
                      </strong>
                    </li>
                  `,
                )
                .join("")}
            </ol>`
      }
    </section>
  `;
}

function renderFieldGuide(): string {
  return `
    <details class="field-guide">
      <summary>Field guide and public rules</summary>
      <div class="guide-content">
        <p><strong>All outcomes are deterministic.</strong> Identical land produces identical results.</p>
        <ul>
          <li><b>Oats:</b> resilient and locally informative; rarely spectacular.</li>
          <li><b>Wheat:</b> profitable in deep alluvial loam, costly on difficult ground.</li>
          <li><b>Alfalfa:</b> sacrifices income to read deep obstruction and neighboring moisture.</li>
          <li><b>Survey:</b> exact local evidence without finishing the location.</li>
          <li><b>Excavate:</b> definitive but terminal; empty ground remains disturbed.</li>
          <li><b>Preserve:</b> terminal and paid only when the undisturbed land has recognized value. Every true former-watercourse location qualifies for a conservation easement.</li>
        </ul>
        <table class="yield-guide">
          <thead><tr><th>Known soil</th><th>Oats</th><th>Wheat</th><th>Alfalfa</th></tr></thead>
          <tbody>
            <tr><th>Saturated fine silt</th><td>3¤</td><td>1¤</td><td>2¤</td></tr>
            <tr><th>Deep alluvial loam</th><td>7¤</td><td>14¤</td><td>6¤</td></tr>
            <tr><th>Ordinary brown loam</th><td>5¤</td><td>8¤</td><td>4¤</td></tr>
            <tr><th>Compacted worked earth</th><td>2¤</td><td>2¤</td><td>1¤</td></tr>
            <tr><th>Thin soil over rock</th><td>2¤</td><td>1¤</td><td>1¤</td></tr>
          </tbody>
        </table>
        <p>Former water influences nearby soil. Roads compact ground in connected lines. Human places tend to relate to water and routes rather than appearing randomly.</p>
        <p><strong>Worked example:</strong> lush grass beside a damp swale supports a nearby-water hypothesis, but not necessarily a mark on both locations. Compare the surrounding line, then survey the ambiguous location if the evidence still supports more than one explanation.</p>
        <p><strong>Keyboard:</strong> Tab enters the field at the selected location; arrow keys move between neighboring hexes. Enter or Space selects. Journal marks support Ctrl/Cmd+Z, Ctrl/Cmd+Shift+Z, and Ctrl+Y.</p>
        <p><strong>Some difficult locations have no profitable agricultural use.</strong> Their puzzle is to identify the least costly appropriate use while earning enough elsewhere to complete the field.</p>
      </div>
    </details>
  `;
}

function renderProgressFooter(): string {
  const remaining = SITE.cells.length - terminalCount();
  if (phase === "complete") {
    return `
      <section class="completion-panel is-ready">
        <div>
          <p class="eyebrow">Completed field</p>
          <h2>The history is available for study</h2>
          <p>Select any location for its full revealed analysis, or reopen the balance sheet and grade.</p>
        </div>
        <button class="primary-button" data-open-results type="button">View final report</button>
      </section>
    `;
  }
  return `
    <section class="completion-panel ${remaining === 0 ? "is-ready" : ""}">
      <div>
        <p class="eyebrow">Completion</p>
        <h2>${remaining === 0 ? "The field is fully allocated" : `${remaining} locations remain`}</h2>
        <p>
          ${
            remaining === 0
              ? "Review your free Journal marks, then reveal the land's history."
              : "Every location must be planted, excavated, or preserved."
          }
        </p>
      </div>
      <button
        class="primary-button"
        data-finish-field
        type="button"
        ${remaining === 0 && phase === "playing" ? "" : "disabled"}
      >Complete field and reveal history</button>
    </section>
  `;
}

function journalMarkCount(): number {
  return Object.values(journal).reduce((total, marks) => total + marks.size, 0);
}

function renderJournalWarning(): string {
  const count = journalMarkCount();
  return `
    <div class="modal-backdrop">
      <section class="result-modal warning-modal" role="dialog" aria-modal="true" aria-labelledby="journal-warning-title">
        <p class="eyebrow">Historical reconstruction check</p>
        <h2 id="journal-warning-title">Your Field Journal has only ${count} ${count === 1 ? "mark" : "marks"}</h2>
        <p>
          The final reconstruction grade compares your watercourse, old-road, and
          point-of-interest hypotheses with the true history. You can submit now,
          but an empty or sparse Journal will receive a low reconstruction score.
        </p>
        <div class="modal-actions">
          <button class="primary-button" data-return-to-journal type="button">Return to the Journal</button>
          <button class="quiet-button" data-confirm-finish type="button">Submit anyway</button>
        </div>
      </section>
    </div>
  `;
}

function renderResetConfirmation(): string {
  const restarting = resetIntent === "restart";
  return `
    <div class="modal-backdrop">
      <section class="result-modal warning-modal" role="dialog" aria-modal="true" aria-labelledby="reset-title">
        <p class="eyebrow">Confirm discarded progress</p>
        <h2 id="reset-title">${restarting ? "Restart this field?" : "Start a new field?"}</h2>
        <p>
          ${
            restarting
              ? "This clears every action and Journal mark in the current attempt."
              : "This replaces the saved field, including its actions, Journal marks, and report."
          }
        </p>
        <div class="modal-actions">
          <button class="primary-button" data-confirm-reset type="button">${restarting ? "Restart field" : "Discard and continue"}</button>
          <button class="quiet-button" data-cancel-reset type="button">Keep current field</button>
        </div>
      </section>
    </div>
  `;
}

function renderFailure(): string {
  return `
    <div class="modal-backdrop">
      <section class="result-modal failure-modal" role="dialog" aria-modal="true" aria-labelledby="failure-title">
        <p class="eyebrow">Attempt concluded</p>
        <h2 id="failure-title">The field is insolvent</h2>
        <p>
          ${SITE.cells.length - terminalCount()} locations remain, but ${formatFunds(funds)}
          cannot pay for the least expensive terminal action. The hidden history has
          not been revealed.
        </p>
        <dl class="failure-summary">
          <div><dt>Opening funds</dt><dd>${formatFunds(openingFunds)}</dd></div>
          <div><dt>Closing funds</dt><dd>${formatFunds(funds)}</dd></div>
          <div><dt>Locations finished</dt><dd>${terminalCount()} / ${SITE.cells.length}</dd></div>
        </dl>
        <div class="modal-actions">
          <button class="quiet-button" data-review-failure type="button">Review this attempt</button>
          <button class="primary-button" data-restart type="button">Restart this attempt</button>
          <button class="quiet-button" data-new-game type="button">Choose another difficulty</button>
        </div>
      </section>
    </div>
  `;
}

function renderResults(result: SiteResult): string {
  return `
    <div class="modal-backdrop result-backdrop">
      <section class="result-modal" role="dialog" aria-modal="true" aria-labelledby="result-title">
        <div class="result-hero">
          <div class="grade-medallion" aria-label="Overall grade ${result.grade}">
            <span>Grade</span><strong>${result.grade}</strong>
          </div>
          <div>
            <p class="eyebrow">Field completed</p>
            <h2 id="result-title">The history of ${escapeHtml(SITE.title)}</h2>
            <p>${escapeHtml(SITE.historicalAccount)}</p>
          </div>
        </div>
        <div class="result-columns">
          <section>
            <h3>Balance sheet</h3>
            <table>
              <tbody>
                <tr><th>Opening funds</th><td>${formatFunds(result.openingFunds)}</td></tr>
                <tr><th>Total spent</th><td>−${formatFunds(result.totalSpent)}</td></tr>
                <tr><th>Agricultural revenue</th><td>+${formatFunds(result.agriculturalRevenue)}</td></tr>
                <tr><th>Discovery and preservation</th><td>+${formatFunds(result.discoveryRevenue)}</td></tr>
                <tr class="table-total"><th>Closing funds</th><td>${formatFunds(result.closingFunds)}</td></tr>
                <tr><th>Net change</th><td>${formatSignedFunds(result.netChange)}</td></tr>
              </tbody>
            </table>
          </section>
          <section>
            <h3>Final site</h3>
            <div class="score-bars">
              ${scoreBar("Site value", result.siteQuality, `${result.realizedSiteValue} / ${result.maximumSiteValue}`)}
              ${scoreBar("Field Journal accuracy", result.historyAccuracy, percent(result.historyAccuracy))}
              ${scoreBar("Discoveries", result.discoveryRate, `${result.foundDiscoveries.length} / ${SITE.pointsOfInterest.length}`)}
              ${scoreBar("Overall", result.overallScore, percent(result.overallScore))}
            </div>
          </section>
        </div>
        <section class="history-breakdown">
          <h3>Field Journal comparison</h3>
          <div>
            ${result.layers
              .map(
                (layer) => `
                  <article>
                    <strong>${escapeHtml(layer.title)}</strong>
                    <span>${percent(layer.accuracy)}</span>
                    <p>${layer.correct} correct · ${layer.missed} missed · ${layer.incorrect} unsupported</p>
                  </article>
                `,
              )
              .join("")}
          </div>
        </section>
        <section class="discoveries-result">
          <h3>Discoveries</h3>
          ${
            result.foundDiscoveries.length === 0
              ? `<p>No points of interest were recovered or correctly preserved. ${result.undiscoveredCount} remained undiscovered.</p>`
              : `
                <ul>
                  ${result.foundDiscoveries
                    .map(
                      (point) => `<li><strong>${escapeHtml(point.title)}</strong> — ${escapeHtml(point.description)}</li>`,
                    )
                    .join("")}
                </ul>
                <p>${result.undiscoveredCount} of ${SITE.pointsOfInterest.length} points of interest remained undiscovered.</p>
              `
          }
        </section>
        <div class="result-legend" aria-label="Historical overlay legend">
          <span><i class="line-key water-line-key"></i><b>Blue:</b> former watercourse</span>
          <span><i class="line-key road-line-key"></i><b>Brown dashed:</b> old farm road</span>
          <span><i class="key-mark interest-key">✦</i><b>Gold:</b> point of interest</span>
        </div>
        <div class="modal-actions">
          <button class="primary-button" data-restart type="button">Replay as a standalone field</button>
          <button class="quiet-button" data-close-results type="button">Review the revealed map</button>
        </div>
      </section>
    </div>
  `;
}

function scoreBar(label: string, value: number, display: string): string {
  const clamped = Math.max(0, Math.min(100, value));
  return `
    <div class="score-row">
      <div><span>${escapeHtml(label)}</span><strong>${escapeHtml(display)}</strong></div>
      <div class="bar"><i style="width: ${clamped}%"></i></div>
    </div>
  `;
}

function renderGame(): string {
  return `
    ${renderHeader()}
    <main class="game-shell">
      <section class="intro-strip">
        <p>${escapeHtml(SITE.introduction)}</p>
        <p class="status-message" aria-live="polite">${escapeHtml(statusMessage)}</p>
      </section>
      <div class="play-layout">
        ${renderMap()}
        <aside class="side-stack">
          ${renderSelectedLocation()}
          ${renderLedger()}
          ${renderFieldGuide()}
        </aside>
      </div>
      ${renderProgressFooter()}
    </main>
    ${phase === "failed" && failureOpen ? renderFailure() : ""}
    ${finishWarningOpen ? renderJournalWarning() : ""}
    ${resetIntent === undefined ? "" : renderResetConfirmation()}
    ${
      phase === "complete" && finalResult !== undefined && resultsOpen
        ? renderResults(finalResult)
        : ""
    }
  `;
}

function focusSelectorFor(element: Element | null): string | undefined {
  if (element === null || !app.contains(element)) return undefined;
  if (element.id !== "") return `#${CSS.escape(element.id)}`;
  const attributes = [
    "data-cell-key",
    "data-action",
    "data-journal-layer",
    "data-theme-control",
    "data-new-game",
    "data-hint",
    "data-open-results",
  ];
  for (const attribute of attributes) {
    if (element.hasAttribute(attribute)) {
      const value = element.getAttribute(attribute);
      return value === ""
        ? `[${attribute}]`
        : `[${attribute}="${CSS.escape(value ?? "")}"]`;
    }
  }
  return undefined;
}

function focusElement(selector: string | undefined): void {
  if (selector === undefined) return;
  const target = app.querySelector<Element & { focus: () => void }>(selector);
  target?.focus();
}

function render(focusSelector?: string): void {
  const preservedFocus =
    focusSelector ?? focusSelectorFor(document.activeElement);
  persistGame();
  app.innerHTML = phase === "setup" ? renderSetup() : renderGame();
  const dialog = app.querySelector<HTMLElement>('[role="dialog"]');
  if (dialog !== null) {
    const firstControl = dialog.querySelector<HTMLElement>(
      "button:not(:disabled), select:not(:disabled), [tabindex]:not([tabindex='-1'])",
    );
    (firstControl ?? dialog).focus();
    return;
  }
  focusElement(preservedFocus);
}

function recordLedger(
  cell: Cell,
  action: ActionId,
  description: string,
  spent: number,
  earned: number,
): void {
  ledger.push({
    cellKey: cell.key,
    label: cell.label,
    action,
    description,
    spent,
    earned,
  });
}

function executePendingAction(): void {
  if (pendingAction === undefined || phase !== "playing") return;
  const actionId = pendingAction;
  const cell = selectedCell();
  const entry = knowledgeFor(cell.key);
  const action = ACTIONS[actionId];
  pendingAction = undefined;

  if (entry.terminal !== undefined || funds < action.cost) {
    statusMessage = "That action is no longer available.";
    render();
    return;
  }

  if (actionId === "survey") {
    if (entry.surveyed) {
      statusMessage = `${cell.label} has already received its low-impact survey.`;
      render();
      return;
    }
    const survey = resolveSurvey(cell, SITE);
    funds -= survey.cost;
    knowledge.set(cell.key, {
      surveyed: true,
      surveyEvidence: survey.evidence,
    });
    recordLedger(cell, actionId, "low-impact survey", survey.cost, 0);
    statusMessage = `${cell.label} was surveyed. It remains available for a terminal use.`;
  } else {
    const terminal = resolveTerminalAction(
      actionId as TerminalActionId,
      cell,
      SITE,
    );
    funds = funds - terminal.cost + terminal.revenue;
    knowledge.set(cell.key, {
      surveyed: entry.surveyed,
      surveyEvidence: entry.surveyEvidence,
      terminal,
    });
    recordLedger(
      cell,
      actionId,
      terminal.outcomeTitle,
      terminal.cost,
      terminal.revenue,
    );
    statusMessage = `${cell.label}: ${terminal.outcomeTitle}. Net ${formatSignedFunds(
      terminal.revenue - terminal.cost,
    )}.`;
  }
  committedActions.push({ cellKey: cell.key, action: actionId });

  if (!allLocationsTerminal() && funds < MINIMUM_TERMINAL_COST) {
    phase = "failed";
    failureOpen = true;
  } else if (allLocationsTerminal()) {
    statusMessage =
      "Every location has a terminal use. Finish your Journal marks and reveal the history.";
  }
  render();
}

function toggleJournal(layer: JournalLayer): void {
  if (phase !== "playing") return;
  const layerSet = journal[layer];
  const wasMarked = layerSet.has(selectedKey);
  journalUndo.push({ layer, cellKey: selectedKey, wasMarked });
  journalRedo = [];
  if (wasMarked) {
    layerSet.delete(selectedKey);
  } else {
    layerSet.add(selectedKey);
  }
  statusMessage = `Updated the free Field Journal marks for ${selectedCell().label}.`;
  render();
}

function applyJournalEdit(edit: JournalEdit, marked: boolean): void {
  const layerSet = journal[edit.layer];
  if (marked) {
    layerSet.add(edit.cellKey);
  } else {
    layerSet.delete(edit.cellKey);
  }
  selectedKey = edit.cellKey;
}

function undoJournal(): void {
  if (phase !== "playing") return;
  const edit = journalUndo.pop();
  if (edit === undefined) return;
  applyJournalEdit(edit, edit.wasMarked);
  journalRedo.push(edit);
  statusMessage = `Undid the last Field Journal edit at ${selectedCell().label}.`;
  render("[data-journal-undo]");
}

function redoJournal(): void {
  if (phase !== "playing") return;
  const edit = journalRedo.pop();
  if (edit === undefined) return;
  applyJournalEdit(edit, !edit.wasMarked);
  journalUndo.push(edit);
  statusMessage = `Redid the Field Journal edit at ${selectedCell().label}.`;
  render("[data-journal-redo]");
}

function requestReset(intent: ResetIntent, returnFocus?: string): void {
  resetIntent = intent;
  resetReturnFocus = returnFocus;
  render();
}

function cancelReset(): void {
  resetIntent = undefined;
  const returnFocus = resetReturnFocus;
  resetReturnFocus = undefined;
  render(returnFocus);
}

function confirmReset(): void {
  const intent = resetIntent;
  resetIntent = undefined;
  resetReturnFocus = undefined;
  clearSavedGame();
  if (intent === "restart") {
    startGame(difficulty);
    return;
  }
  phase = "setup";
  finalResult = undefined;
  resultsOpen = false;
  finishWarningOpen = false;
  failureOpen = false;
  pendingAction = undefined;
  render("[data-start-game]");
}

function requestFinish(): void {
  if (!allLocationsTerminal() || phase !== "playing") return;
  if (journalMarkCount() < 3) {
    finishWarningOpen = true;
    render();
    return;
  }
  finishField();
}

function finishField(): void {
  if (!allLocationsTerminal() || phase !== "playing") return;
  finalResult = calculateSiteResult(
    SITE,
    openingFunds,
    knowledge,
    ledger,
    currentJournal(),
  );
  phase = "complete";
  pendingAction = undefined;
  finishWarningOpen = false;
  resultsOpen = true;
  statusMessage =
    "The true history is now overlaid on the field for comparison with your Journal.";
  render();
}

document.addEventListener("click", (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const difficultyInput = target.closest<HTMLInputElement>(
    'input[name="difficulty"]',
  );
  if (difficultyInput !== null) {
    difficulty = difficultyInput.value as Difficulty;
    render();
    return;
  }

  if (target.closest("[data-start-game]") !== null) {
    if (savedGame !== undefined) {
      requestReset("new", "[data-start-game]");
      return;
    }
    startGame(difficulty);
    return;
  }
  if (target.closest("[data-continue-game]") !== null) {
    if (continueSavedGame()) {
      render(`[data-cell-key="${CSS.escape(selectedKey)}"]`);
    } else {
      statusMessage = "The saved field was incompatible and was safely discarded.";
      render("[data-start-game]");
    }
    return;
  }

  const cellTarget = target.closest<SVGGElement>("[data-cell-key]");
  if (cellTarget !== null && phase !== "setup") {
    const key = cellTarget.dataset.cellKey;
    if (key !== undefined) {
      selectedKey = key;
      pendingAction = undefined;
      render(`[data-cell-key="${CSS.escape(key)}"]`);
    }
    return;
  }

  const actionTarget = target.closest<HTMLButtonElement>("[data-action]");
  if (actionTarget !== null) {
    pendingAction = actionTarget.dataset.action as ActionId;
    render("[data-confirm-action]");
    return;
  }

  if (target.closest("[data-confirm-action]") !== null) {
    executePendingAction();
    return;
  }
  if (target.closest("[data-cancel-action]") !== null) {
    const actionId = pendingAction;
    pendingAction = undefined;
    render(
      actionId === undefined ? undefined : `[data-action="${actionId}"]`,
    );
    return;
  }

  const journalTarget = target.closest<HTMLButtonElement>(
    "[data-journal-layer]",
  );
  if (journalTarget !== null) {
    toggleJournal(journalTarget.dataset.journalLayer as JournalLayer);
    return;
  }
  if (target.closest("[data-journal-undo]") !== null) {
    undoJournal();
    return;
  }
  if (target.closest("[data-journal-redo]") !== null) {
    redoJournal();
    return;
  }
  if (target.closest("[data-hint]") !== null) {
    const nextStep = Math.min(3, (hintSteps.get(selectedKey) ?? 0) + 1);
    hintSteps.set(selectedKey, nextStep);
    statusMessage = `Displayed reasoning hint ${nextStep} of 3 for ${selectedCell().label}.`;
    render("[data-hint]");
    return;
  }

  if (target.closest("[data-finish-field]") !== null) {
    requestFinish();
    return;
  }
  if (target.closest("[data-confirm-finish]") !== null) {
    finishField();
    return;
  }
  if (target.closest("[data-return-to-journal]") !== null) {
    finishWarningOpen = false;
    render();
    return;
  }
  if (target.closest("[data-restart]") !== null) {
    requestReset("restart", "[data-restart]");
    return;
  }
  if (target.closest("[data-new-game]") !== null) {
    requestReset("new", "[data-new-game]");
    return;
  }
  if (target.closest("[data-confirm-reset]") !== null) {
    confirmReset();
    return;
  }
  if (target.closest("[data-cancel-reset]") !== null) {
    cancelReset();
    return;
  }
  if (target.closest("[data-review-failure]") !== null) {
    failureOpen = false;
    statusMessage = "Review the evidence and ledger from this attempt; hidden history remains concealed.";
    render(`[data-cell-key="${CSS.escape(selectedKey)}"]`);
    return;
  }
  if (target.closest("[data-open-results]") !== null) {
    if (phase === "complete" && finalResult !== undefined) {
      resultsOpen = true;
      render();
    }
    return;
  }
  if (target.closest("[data-close-results]") !== null) {
    resultsOpen = false;
    render("[data-open-results]");
  }
});

document.addEventListener("change", (event) => {
  const target = event.target;
  if (!(target instanceof HTMLSelectElement) || !target.matches("[data-theme-control]")) {
    return;
  }
  themePreference = parseThemePreference(target.value);
  writeStorage(THEME_STORAGE_KEY, themePreference);
  applyTheme();
  render("[data-theme-control]");
});

document.addEventListener("keydown", (event) => {
  const target = event.target;

  if (event.key === "Escape") {
    if (resetIntent !== undefined) {
      event.preventDefault();
      cancelReset();
    } else if (finishWarningOpen) {
      event.preventDefault();
      finishWarningOpen = false;
      render("[data-finish-field]");
    } else if (resultsOpen) {
      event.preventDefault();
      resultsOpen = false;
      render("[data-open-results]");
    } else if (failureOpen) {
      event.preventDefault();
      failureOpen = false;
      render(`[data-cell-key="${CSS.escape(selectedKey)}"]`);
    } else if (pendingAction !== undefined) {
      event.preventDefault();
      const actionId = pendingAction;
      pendingAction = undefined;
      render(`[data-action="${actionId}"]`);
    }
    return;
  }

  const dialog = app.querySelector<HTMLElement>('[role="dialog"]');
  if (event.key === "Tab" && dialog !== null) {
    const controls = [...dialog.querySelectorAll<HTMLElement>(
      "button:not(:disabled), select:not(:disabled), a[href], [tabindex]:not([tabindex='-1'])",
    )];
    if (controls.length === 0) return;
    const first = controls[0];
    const last = controls.at(-1);
    if (first === undefined || last === undefined) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
    return;
  }

  const modifier = event.ctrlKey || event.metaKey;
  if (
    modifier &&
    phase === "playing" &&
    !(target instanceof HTMLInputElement) &&
    !(target instanceof HTMLTextAreaElement) &&
    !(target instanceof HTMLSelectElement)
  ) {
    const key = event.key.toLowerCase();
    if (key === "z" && !event.shiftKey) {
      event.preventDefault();
      undoJournal();
      return;
    }
    if ((key === "z" && event.shiftKey) || key === "y") {
      event.preventDefault();
      redoJournal();
      return;
    }
  }

  if (!(target instanceof SVGGElement) || !target.matches("[data-cell-key]")) {
    return;
  }

  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    const key = target.dataset.cellKey;
    if (key !== undefined) {
      selectedKey = key;
      pendingAction = undefined;
      render(`[data-cell-key="${CSS.escape(key)}"]`);
    }
    return;
  }

  const directionByKey: Readonly<Record<string, number>> = {
    ArrowRight: 0,
    ArrowUp: 2,
    ArrowLeft: 3,
    ArrowDown: 5,
  };
  const directionIndex = directionByKey[event.key];
  let nextKey: string | undefined;
  if (directionIndex !== undefined) {
    const direction = AXIAL_DIRECTIONS[directionIndex];
    const current = SITE.cellsByKey.get(selectedKey);
    if (direction !== undefined && current !== undefined) {
      nextKey = coordKey(addCoords(current.coord, direction));
    }
  } else if (event.key === "Home") {
    nextKey = SITE.cells[0]?.key;
  } else if (event.key === "End") {
    nextKey = SITE.cells.at(-1)?.key;
  }

  if (nextKey !== undefined && SITE.cellsByKey.has(nextKey)) {
    event.preventDefault();
    selectedKey = nextKey;
    pendingAction = undefined;
    render(`[data-cell-key="${CSS.escape(nextKey)}"]`);
  }
});

// Expose a small read-only diagnostic surface for the browser smoke test.
Object.defineProperty(window, "__WRR_DIAGNOSTICS__", {
  value: {
    getState: () => ({
      phase,
      difficulty,
      funds,
      openingFunds,
      terminalCount: terminalCount(),
      totalLocations: SITE.cells.length,
      selectedKey,
      resultsOpen,
      journalMarks: journalMarkCount(),
      journalUndo: journalUndo.length,
      journalRedo: journalRedo.length,
      themePreference,
      resolvedTheme: resolvedTheme(themePreference),
      hasSavedGame: readStorage(SAVE_STORAGE_KEY) !== null,
      maximumSelectedValue: maximumCellValue(selectedCell(), SITE),
    }),
  },
  writable: false,
});

render();
